import axios, { AxiosResponse, AxiosError } from 'axios'
import axiosRetry from 'axios-retry'
import { datadogLogs } from '@datadog/browser-logs'
import { useCallback } from 'react'
import useCollectDataParameter from './useCollectDataParameter'

export type Callback<T> = (
  payload: T,
  url: string
) => Promise<AxiosResponse<any, any> | void>

export const axiosWithRetry = axios.create()

axiosRetry(axiosWithRetry, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  onRetry(retryCount, error, requestConfig) {
    datadogLogs.logger.warn('Network request failed, retrying', {
      error,
      httpStatusCode: error?.response?.status ?? null,
      requestUrl: requestConfig.url,
      retryCount
    })
  },
  retryCondition(error: AxiosError) {
    const resStatus = error.response?.status ?? 500
    return resStatus >= 500
  }
})

export type HttpMethod = 'post' | 'patch' | 'put' | 'delete'

export const createUseHttpCallback = (
  method: HttpMethod,
  withRetry: boolean
) => {
  const httpClient = withRetry ? axiosWithRetry : axios

  const useHttpCallback: <T>() => Callback<T> = <T>() => {
    const collectData = useCollectDataParameter()
    const httpRequest = useCallback<Callback<T>>((payload, url) => {
      return httpClient[method](url, payload as any)
    }, [])
    const httpRequestStub = useCallback(() => Promise.resolve(), [])

    return collectData ? httpRequest : httpRequestStub
  }

  return useHttpCallback
}
